import { ADULTS_KEY } from '@yoyoclub/shared';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import PickCard, { type PickCardData } from '../components/picks/PickCard';
import { PicksFilters, PicksFiltersSheet } from '../components/picks/PicksFilters';
import PicksMark from '../components/picks/PicksMark';
import { PicksNote } from '../components/picks/PicksNote';
import {
  picksFilterCount,
  picksFiltersFromUrl,
  picksFiltersToUrl,
  type PicksFilterState,
} from '../components/picks/picksFilterState';
import { btnGhost, btnPrimary } from '../components/ui/redesign';
import { resolveTimePreset } from '../utils/whenRange';

interface PicksPage {
  items: PickCardData[];
  total: number;
  page: number;
  hasMore: boolean;
  categories: string[];
}

/**
 * /picks (PRD §5.4). Filters use the All Activities look and live in the URL, so the
 * homepage "查看全部" link lands with the same age, time and places, each removable here.
 */
export default function PicksList() {
  const { t } = useTranslation(['picks', 'home', 'common', 'activities']);
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => picksFiltersFromUrl(params), [params]);
  const [sheetOpen, setSheetOpen] = useState(false);

  const [pages, setPages] = useState<PicksPage[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (filters.audience === ADULTS_KEY) q.set('audience', ADULTS_KEY);
    else if (filters.audience) q.set('stage', filters.audience);
    if (filters.categories.length) q.set('category', filters.categories.join(','));
    if (filters.languages.length) q.set('lang', filters.languages.join(','));
    if (filters.participations.length) q.set('participation', filters.participations.join(','));
    const range = resolveTimePreset(filters.when);
    if (range.from) q.set('dateFrom', range.from);
    if (range.to) q.set('dateTo', range.to);
    if (filters.regions.length) q.set('regions', filters.regions.join(','));
    return q.toString();
  }, [filters]);

  // A new filter starts over at page 1.
  useEffect(() => {
    setPages([]);
    setPage(1);
  }, [query]);

  useEffect(() => {
    const controller = new AbortController();
    const api = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    setLoading(true);
    setFailed(false);
    fetch(`${api}/picks?${query}${query ? '&' : ''}page=${page}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((res) => {
        if (!res?.success) throw new Error('load failed');
        setPages((prev) => [...prev.filter((p) => p.page < page), res.data]);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, page]);

  const applyFilters = (next: PicksFilterState) =>
    setParams(picksFiltersToUrl(next, params), { replace: true });
  const filterCount = picksFilterCount(filters);

  const items = pages.flatMap((p) => p.items);
  const last = pages[pages.length - 1];
  const categories = pages[0]?.categories ?? [];

  return (
    <div className="bg-warm-white font-text text-[15px] leading-[1.6] text-ink">
      <Helmet>
        <title>{t('picks:meta.title')}</title>
        <meta name="description" content={t('picks:meta.description')} />
      </Helmet>
      <section className="mx-auto max-w-[1160px] px-4 pb-[50px] desk:px-7">
        <div className="flex flex-wrap items-end justify-between gap-4 pb-2 pt-[34px]">
          <div>
            <PicksMark />
            <h1 className="mt-1 font-ui text-[clamp(28px,3.2vw,40px)] font-bold leading-[1.6] text-ink [text-wrap:balance]">
              {t('picks:name')}
            </h1>
            <PicksNote className="mt-2.5" />
          </div>
          <Link to="/picks/submit" className={btnPrimary}>
            <Plus className="h-[18px] w-[18px]" aria-hidden="true" />
            {t('picks:list.suggest')}
          </Link>
        </div>

        {/* The All Activities filter look: pill dropdowns, a sheet on phones */}
        <div className="mt-4 hidden md:block">
          <PicksFilters value={filters} categories={categories} onChange={applyFilters} />
        </div>
        <div className="mt-4 flex justify-end md:hidden">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              filterCount > 0
                ? 'border-primary bg-primary-pale text-primary'
                : 'border-soft-gray bg-white text-charcoal'
            }`}
          >
            {filterCount > 0
              ? t('activities:filters.mobileButton', { count: filterCount })
              : t('activities:filters.mobileButtonNone')}
          </button>
        </div>
        <PicksFiltersSheet
          open={sheetOpen}
          value={filters}
          categories={categories}
          onClose={() => setSheetOpen(false)}
          onApply={applyFilters}
        />

        <div className="mt-[22px] grid grid-cols-1 gap-4 desk:grid-cols-[repeat(auto-fill,minmax(236px,1fr))]">
          {items.map((pick) => (
            <PickCard key={pick.id} pick={pick} />
          ))}
          {!loading && !failed && items.length === 0 && (
            <div className="rounded-2xl bg-white p-[26px] text-center text-ink-2 desk:col-span-full">
              {t('picks:list.empty')}
            </div>
          )}
          {failed && (
            <div className="rounded-2xl bg-white p-[26px] text-center text-ink-2 desk:col-span-full">
              {t('picks:list.loadFailed')}
            </div>
          )}
        </div>
        {last?.hasMore && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              className={btnGhost}
              disabled={loading}
              onClick={() => setPage(last.page + 1)}
            >
              {t('picks:list.loadMore')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
